import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';

import { UserProvider } from './context/UserContext';
import { setupAxiosAuth } from './api/setupAxiosAuth';
import { initSessionManager, hasActiveSession } from './utils/sessionManager';
import {
  ensureMessagingServiceWorker,
  requestPermissionAndSendToken,
  setupForegroundNotification,
} from './firebase-config';

// Restaurant Header
import Header from './restaurant/Header';

// Restaurant Pages
import Signup from './restaurant/Signup'
import Signin from './restaurant/Signin';
import Home from './restaurant/Home';
import Supervisor from './restaurant/Supervisor';
import OrderDetails from './restaurant/OrderDetails';
import BusinessProfilePage from './restaurant/BusinessProfilePage';
import UnnoticedPassword from './restaurant/RestaurantForgotpassword';
import Employee from './restaurant/Employee';
import Receipt from './restaurant/Receipt';
import InventoryItemDetail from './restaurant/InventoryItemDetail';
import SupplierDetail from './restaurant/SupplierDetail';
import PurchaseDetail from './restaurant/PurchaseDetail';
import StoreOrderDetail from './restaurant/StoreOrderDetail';
import CateringQuoteDetail from './restaurant/CateringQuoteDetail';
import CreateCateringQuote from './restaurant/CreateCateringQuote';
import CreatePartyHallQuote from './restaurant/CreatePartyHallQuote';
import PartyHallQuoteDetail from './restaurant/PartyHallQuoteDetail';
import HiringCandidateDetail from './restaurant/HiringCandidateDetail';
import BuyGrocery from './restaurant/BuyGrocery';
import BuyGroceryCart from './restaurant/BuyGroceryCart';
import RestaurantDetail from './restaurant/RestaurantDetail';
import CustomerFacingProfile from './restaurant/CustomerFacingProfile';
import StoreDetail from './store/StoreDetail';
import MenuTemplateDetail from './restaurant/MenuTemplateDetail';
import CateringTemplateDetail from './restaurant/CateringTemplateDetail';
import FoodItemRecipePage from './restaurant/FoodItemRecipePage';
import FoodItemDetailPage from './restaurant/FoodItemDetailPage';
import AppUsersPage from './admin/AppUsersPage';
import AppUserProfilePage from './admin/AppUserProfilePage';
import PartnerLogin, { PARTNER_LOGIN_PATH } from './partner/PartnerLogin';
import PartnerChooseBusiness from './partner/PartnerChooseBusiness';
import { PARTNER_CHOOSE_BUSINESS_PATH } from './partner/partnerSession';
import { ROUTES } from './constants/appRoutes';
import EatsAppLogin, {
  EATS_BUSINESS_LOGIN_PATH,
  EATS_APP_ADMIN_LOGIN_PATH,
  EATS_APP_USER_LOGIN_PATH,
} from './admin/EatsAppLogin';
import EatsAdminDashboard from './admin/EatsAdminDashboard';
import EatsAdminCreateRestaurantPage from './admin/EatsAdminCreateRestaurantPage';
import EatsAdminCreateOrganizationPage from './admin/EatsAdminCreateOrganizationPage';
import EatsAdminOrganizationDetailPage from './admin/EatsAdminOrganizationDetailPage';
import EatsAdminRestaurantDetailPage from './admin/EatsAdminRestaurantDetailPage';
import EatsAdminCreateStorePage from './admin/EatsAdminCreateStorePage';
import EatsAdminStoreDetailPage from './admin/EatsAdminStoreDetailPage';
import EatsAppRestaurantsPage from './admin/EatsAppRestaurantsPage';

// organization Pages
import OrganizationRegister from './organization/SignUp';
import OrganizationLogin from './organization/SignIn';
import Organization from './organization/Header'; 
import AddorganizationHomePage from './organization/OrganizationHome';
import Support from './organization/Support';
import Forgotpasswords from './organization/ForgotPassword';
import EmployeeHomePage from './organization/Employee';
import Dashboard from './organization/Dashboard';

// School Book
import ClassBookHome from './classbook/Homepage/ClassBookHome';
import ClassBookNavbar from './classbook/ClassBookNavbar/ClassBookNavabar';
import ClassBookProfile from './classbook/ClassBookProfilePage/ClassBookProfile';
import SearchSchools from './classbook/SearchSchools/SearchSchools';
import SearchFriends from './classbook/SearchFriends/SearchFriends';
import CBDashboard from './classbook/ClassBookDashBoardPage/CBDashBoard';
import ProfileUpdate from './classbook/ProfileUpdate/ProfileUpdate';
import MyFriends from './classbook/MyFriends/MyFriends';
import MySchools from './classbook/MySchools/MySchools';
import AddClass from './classbook/AddClass/AddClass';
import Teachers from './classbook/Teachers/Teachers';
import Videos from './classbook/Videos/Videos';
import Photos from './classbook/Photos/Photos';
import Comments from './classbook/Comments/Comments';

import './App.css';

setupAxiosAuth();

function PlaceOrderRedirect() {
  const location = useLocation();
  return <Navigate to="/business/admin?tab=dine-in" replace state={location.state} />;
}

const App = () => {
  useEffect(() => {
    ensureMessagingServiceWorker();
  }, []);

  return (
    <UserProvider>
      <Router>
        <Main />
        {/* Class Book */}
        {/* <ClassBookNavbar/>
        <Routes>
           
          
           <Route path='/' element={<ClassBookHome/>} />
             <Route path='/searchschools' element={<SearchSchools/>} />
           <Route path='/searchfriend' element={<SearchFriends/>} /> 
           <Route path='/classBookProfile' element={<ClassBookProfile/>} />
           <Route path='/classBookDashboard' element={<CBDashboard/>} />
           <Route path='/profileUpdate' element={<ProfileUpdate/>} />
           <Route path='/myFriends' element={<MyFriends/>} />
           <Route path='/mySchools' element={<MySchools/>} />
           <Route path='/addClass' element={<AddClass/>} />
           <Route path='/teachers' element={<Teachers/>} />
           <Route path='/videos' element={<Videos/>} />
           <Route path='/photos' element={<Photos/>} />
           <Route path='/comments' element={<Comments/>} />
        </Routes> */}
      </Router>
    </UserProvider>
  );

};//App

/** Redirect bookmarks from /eats-app/admin/* → /eats/admin/* */
const LegacyEatsAppAdminRedirect = () => {
  const location = useLocation();
  const to =
    location.pathname.replace(/^\/eats-app\/admin/, '/eats/admin') +
    location.search;
  return <Navigate to={to} replace />;
};

/** Redirect old eats-business admin URLs → /eats/admin/* */
const LegacyEatsBusinessAdminRedirect = () => {
  const location = useLocation();
  let path = location.pathname;
  if (path === '/eats-business/admin/dashboard' || path === '/eats-business/admin') {
    path = '/eats/admin';
  } else {
    path = path.replace(/^\/eats-business\/admin/, '/eats/admin');
  }
  return <Navigate to={`${path}${location.search}`} replace />;
};

/** Redirect short /admin and /user bookmarks → /eats/admin and /eats/user */
const LegacyShortEatsRedirect = () => {
  const location = useLocation();
  let path = location.pathname;
  if (path === '/admin' || path.startsWith('/admin/')) {
    path = path.replace(/^\/admin/, '/eats/admin');
  } else if (path === '/user' || path.startsWith('/user/')) {
    path = path.replace(/^\/user/, '/eats/user');
  }
  return <Navigate to={`${path}${location.search}`} replace />;
};

const PreserveSearchRedirect = ({ to }) => {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}`} replace state={location.state} />;
};

/** Redirect old login URLs → unified /partner/login */
const LegacyLoginRedirect = () => <Navigate to={PARTNER_LOGIN_PATH} replace />;

const Main = () => {
  const location = useLocation(); // Access current route
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return hasActiveSession() && localStorage.getItem("isAuthenticated") === "true";
  });
  const [isRegistered, setIsRegistered] = useState(false);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    initSessionManager();
  }, []);

  useEffect(() => {
    const syncAuthFromStorage = () => {
      const authed =
        hasActiveSession() && localStorage.getItem("isAuthenticated") === "true";
      setIsAuthenticated(authed);
    };
    window.addEventListener("storage", syncAuthFromStorage);
    return () => window.removeEventListener("storage", syncAuthFromStorage);
  }, []);

  useEffect(() => {
    const hasSession =
      isAuthenticated || Boolean(localStorage.getItem('Token'));
    if (!hasSession) {
      return undefined;
    }
    const unsubscribe = setupForegroundNotification(setNotification);
    requestPermissionAndSendToken().catch((error) => {
      console.error('[FCM] Registration failed:', error);
    });
    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (!notification) {
      return undefined;
    }
    const timer = window.setTimeout(() => setNotification(null), 8000);
    return () => window.clearTimeout(timer);
  }, [notification]);

  const handleLogin = () => {
    setIsAuthenticated(true);
    localStorage.setItem("isAuthenticated", "true"); // 
  };

  const handleRegistrationSuccess = () => {
    setIsRegistered(true);
    console.log('isRegistered:', isRegistered);
  };

  const getStoredUser = () => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
      return {};
    }
  };

  const isEatsAppAuthenticated = () => {
    const token = localStorage.getItem('Token');
    const user = getStoredUser();
    return !!token && user?.domain === 'EATS_APP';
  };

  const isRootAdmin = () => {
    const user = getStoredUser();
    return String(user?.userRole || user?.role || '').toUpperCase() === 'ROOT_ADMIN';
  };

  const isEatsAppManager = () => {
    const user = getStoredUser();
    if (user?.domain !== 'EATS_APP') return false;
    const role = String(user?.userRole || user?.role || '').toUpperCase();
    return role === 'ROOT_ADMIN' || role === 'APP_ADMIN' || role === 'APP_USER';
  };

  const requirePartnerAuth = (element) => {
    if (!hasActiveSession()) {
      return <Navigate to={PARTNER_LOGIN_PATH} replace />;
    }
    return element;
  };

  const requireEatsAppUser = (element) => {
    if (!isEatsAppAuthenticated()) {
      return <Navigate to={PARTNER_LOGIN_PATH} replace />;
    }
    return element;
  };

  const requireEatsAppManager = (element) => {
    if (!isEatsAppAuthenticated()) {
      return <Navigate to={PARTNER_LOGIN_PATH} replace />;
    }
    if (!isEatsAppManager()) {
      return <Navigate to={PARTNER_LOGIN_PATH} replace />;
    }
    return element;
  };

  const requireRootAdmin = (element) => {
    if (!isEatsAppAuthenticated()) {
      return <Navigate to={PARTNER_LOGIN_PATH} replace />;
    }
    if (!isRootAdmin()) {
      return <Navigate to={ROUTES.admin} replace />;
    }
    return element;
  };


  // Check if the current route is for restaurant pages (and not sign-in or sign-up)
  // eslint-disable-next-line
  const isRestaurantRoute = location.pathname.includes('/restaurant');
  const isorganizationRoute = location.pathname.includes('/organization');

  // Determine whether to show header and footer for restaurant pages
  const isEatsAppAdminRoute =
    location.pathname === '/eats/admin' ||
    location.pathname.startsWith('/eats/admin/') ||
    location.pathname === '/eats/user' ||
    location.pathname.startsWith('/eats/user/') ||
    location.pathname === '/admin' ||
    location.pathname.startsWith('/admin/') ||
    location.pathname === '/user' ||
    location.pathname.startsWith('/user/') ||
    location.pathname.startsWith('/eats-business/admin');
  const isEatsAppShellRoute =
    location.pathname === ROUTES.adminEmployees ||
    location.pathname === ROUTES.userProfile ||
    location.pathname === ROUTES.adminRestaurants ||
    location.pathname.startsWith('/eats/admin/restaurants/') ||
    location.pathname === '/eats-business/users' ||
    location.pathname === '/eats-business/user/profile' ||
    location.pathname === '/eats-business/restaurants' ||
    location.pathname.startsWith('/eats-business/admin/restaurants/');
  const isBusinessPortalRoute =
    location.pathname.startsWith('/business/');
  const isRestaurantAuthPage = 
    location.pathname === '/restaurant/signin' ||
    location.pathname === '/restaurant/signup' ||
    location.pathname === '/restaurant/forgotpassword' ||
    location.pathname === '/restaurant/support' ||
    location.pathname === '/organization/support' ||
    location.pathname === '/support' ||
    location.pathname === ROUTES.support ||
    location.pathname === PARTNER_LOGIN_PATH ||
    location.pathname === PARTNER_CHOOSE_BUSINESS_PATH ||
    location.pathname === EATS_BUSINESS_LOGIN_PATH ||
    location.pathname === EATS_APP_USER_LOGIN_PATH ||
    location.pathname === EATS_APP_ADMIN_LOGIN_PATH;

  const isPublicBusinessProfile =
    /(^|\/)(restaurant|store)\/[^/]+\/profile\/?$/.test(location.pathname) ||
    /(^|\/)business\/\d+\/profile\/?$/.test(location.pathname) ||
    /(^|\/)business\/[^/]+\/\d+\/profile\/?$/.test(location.pathname);

  const showHeaderFooter = 
    !isorganizationRoute &&
    !isRestaurantAuthPage &&
    !isEatsAppAdminRoute &&
    !isEatsAppShellRoute &&
    !isPublicBusinessProfile &&
    location.pathname !== '/' &&
    location.pathname !== '/receipt';
  // Keep restaurant Header on /business/admin and /business/user (same as former /home)

  return (
    <div>
       
    
      {/* Conditionally render Header for restaurant pages, excluding sign-in, sign-up and root */}
      {showHeaderFooter && (
          <Header isAuthenticated={isAuthenticated} />
      )}

     
      {notification && (
        <div className="fcm-toast" role="status" aria-live="polite">
          <div className="fcm-toast__content">
            <p className="fcm-toast__title">{notification.title || 'Notification'}</p>
            <p className="fcm-toast__body">{notification.body}</p>
          </div>
          <button
            type="button"
            className="fcm-toast__close"
            aria-label="Dismiss notification"
            onClick={() => setNotification(null)}
          >
            ×
          </button>
        </div>
      )}
    
      <Routes>        
        <Route path="/" element={<Navigate to={PARTNER_LOGIN_PATH} replace />} />
        <Route path={PARTNER_LOGIN_PATH} element={<PartnerLogin onLogin={handleLogin} />} />
        <Route
          path={PARTNER_CHOOSE_BUSINESS_PATH}
          element={<PartnerChooseBusiness onLogin={handleLogin} />}
        />
        <Route path={EATS_BUSINESS_LOGIN_PATH} element={<LegacyLoginRedirect />} />
        <Route path={EATS_APP_ADMIN_LOGIN_PATH} element={<LegacyLoginRedirect />} />
        <Route path={EATS_APP_USER_LOGIN_PATH} element={<LegacyLoginRedirect />} />
        <Route path="/eats-app/admin/login" element={<LegacyLoginRedirect />} />
        {/* Organization Authentication Routes */}
          <Route path="/organization/signup" element={<OrganizationRegister onSuccess={handleRegistrationSuccess} onLogin={handleLogin} />} />
          <Route path="/organization/signin" element={<LegacyLoginRedirect />} />
          <Route path='/organization/forgotpassword' element={<Forgotpasswords/>} />

         

          {/* Organization Routes should always be accessible */}
          <Route path="/organization/*" element={<Organization isAuthenticated={isAuthenticated} setIsAuthenticated={setIsAuthenticated} />}>
            <Route path="admin" element={isAuthenticated ? <Dashboard /> : <Navigate to={PARTNER_LOGIN_PATH} replace />} />
            <Route path="user" element={isAuthenticated ? <EmployeeHomePage /> : <Navigate to={PARTNER_LOGIN_PATH} replace />} />
            <Route path="add" element={isAuthenticated ? <AddorganizationHomePage /> : <Navigate to={PARTNER_LOGIN_PATH} replace />} />
            <Route path="support" element={<Navigate to={ROUTES.support} replace />} />
            <Route path="dashboard" element={<Navigate to="/organization/admin" replace />} />
            <Route path="employee" element={<Navigate to="/organization/user" replace />} />
          </Route>
        

        {/* Restaurant / Store business portal */}
        <Route path="/restaurant/signup" element={<Signup />} />
        <Route path="/restaurant/signin" element={<LegacyLoginRedirect />} />

        <Route path={ROUTES.businessAdmin} element={requirePartnerAuth(<Home />)} />
        <Route path={ROUTES.businessUser} element={requirePartnerAuth(<Employee />)} />
        <Route path="/home" element={<PreserveSearchRedirect to={ROUTES.businessAdmin} />} />
        <Route path="/user-login" element={<PreserveSearchRedirect to={ROUTES.businessUser} />} />
        <Route path="/supervisor" element={requirePartnerAuth(<Supervisor />)} />
        <Route path="/OrderDetails/:restaurantId/:orderId" element={requirePartnerAuth(<OrderDetails />)} />
        <Route path="/inventory/item/:inventoryItemId" element={requirePartnerAuth(<InventoryItemDetail />)} />
        <Route path="/purchase/buy-grocery/cart" element={requirePartnerAuth(<BuyGroceryCart />)} />
        <Route path="/purchase/buy-grocery" element={requirePartnerAuth(<BuyGrocery />)} />
        <Route path="/purchase/:purchaseId" element={requirePartnerAuth(<PurchaseDetail />)} />
        <Route path="/store-order/:orderId" element={requirePartnerAuth(<StoreOrderDetail />)} />
        <Route path="/catering-quote/create" element={requirePartnerAuth(<CreateCateringQuote />)} />
        <Route path="/party-hall-quote/create" element={requirePartnerAuth(<CreatePartyHallQuote />)} />
        <Route path="/party-hall-quote/:quoteId/edit" element={requirePartnerAuth(<CreatePartyHallQuote />)} />
        <Route path="/catering-quote/:quoteId" element={requirePartnerAuth(<CateringQuoteDetail />)} />
        <Route path="/party-hall-quote/:quoteId" element={requirePartnerAuth(<PartyHallQuoteDetail />)} />
        <Route path="/hiring/candidate/:candidateId" element={requirePartnerAuth(<HiringCandidateDetail />)} />
        <Route path="/supplier/:supplierId" element={requirePartnerAuth(<SupplierDetail />)} />
        <Route path={ROUTES.businessProfile} element={requirePartnerAuth(<BusinessProfilePage />)} />
        <Route path="/profile" element={<Navigate to={ROUTES.businessProfile} replace />} />
        <Route path="/business/profile" element={<Navigate to={ROUTES.businessProfile} replace />} />
        <Route path="/restaurant/:restaurantId" element={requirePartnerAuth(<RestaurantDetail />)} />
        <Route
          path="/restaurant/:restaurantId/profile"
          element={requirePartnerAuth(<CustomerFacingProfile kind="restaurant" />)}
        />
        <Route
          path="/store/:storeId/profile"
          element={requirePartnerAuth(<CustomerFacingProfile kind="store" />)}
        />
        <Route
          path="/business/:businessId/profile"
          element={requirePartnerAuth(<CustomerFacingProfile />)}
        />
        <Route
          path="/business/:entityType/:entityId/profile"
          element={requirePartnerAuth(<CustomerFacingProfile />)}
        />
        <Route path="/store/:storeId" element={requirePartnerAuth(<StoreDetail />)} />
        <Route path="/restaurant/menu-template/:templateId" element={requirePartnerAuth(<MenuTemplateDetail />)} />
        <Route path="/restaurant/catering-package/:cateringTempId" element={requirePartnerAuth(<CateringTemplateDetail />)} />
        <Route path="/restaurant/recipe/:foodItemId/*" element={requirePartnerAuth(<FoodItemRecipePage />)} />
        <Route path="/restaurant/menu-item/:foodItemId" element={requirePartnerAuth(<FoodItemDetailPage />)} />
        <Route path="/place-order" element={<PlaceOrderRedirect />} />
        <Route path="/receipt" element={requirePartnerAuth(<Receipt />)} />
        <Route path='/restaurant/forgotpassword' element={<UnnoticedPassword/>} />
        <Route path={ROUTES.support} element={<Support />} />
        <Route path="/support" element={<Navigate to={ROUTES.support} replace />} />
        <Route path="/restaurant/support" element={<Navigate to={ROUTES.support} replace />} />
        <Route path="/organization/support" element={<Navigate to={ROUTES.support} replace />} />

        {/* Maya Eats Business (EAP) */}
        <Route path="/eats-app/admin/*" element={<LegacyEatsAppAdminRedirect />} />
        <Route path="/eats-business/admin/*" element={<LegacyEatsBusinessAdminRedirect />} />
        <Route path="/admin/*" element={<LegacyShortEatsRedirect />} />
        <Route path="/admin" element={<LegacyShortEatsRedirect />} />
        <Route path="/user/*" element={<LegacyShortEatsRedirect />} />
        <Route path="/user" element={<LegacyShortEatsRedirect />} />
        <Route path={ROUTES.admin} element={requireEatsAppManager(<EatsAdminDashboard />)} />
        <Route path={ROUTES.user} element={requireEatsAppManager(<EatsAdminDashboard />)} />
        <Route path="/eats/admin/restaurants/create" element={requireEatsAppManager(<EatsAdminCreateRestaurantPage />)} />
        <Route path="/eats/admin/restaurants/:restaurantId" element={requireEatsAppManager(<EatsAdminRestaurantDetailPage />)} />
        <Route path="/eats/admin/organizations/create" element={requireEatsAppManager(<EatsAdminCreateOrganizationPage />)} />
        <Route path="/eats/admin/organizations/:organizationId" element={requireEatsAppManager(<EatsAdminOrganizationDetailPage />)} />
        <Route path="/eats/admin/stores/create" element={requireEatsAppManager(<EatsAdminCreateStorePage />)} />
        <Route path="/eats/admin/stores/:storeId" element={requireEatsAppManager(<EatsAdminStoreDetailPage />)} />
        <Route path={ROUTES.adminEmployees} element={requireRootAdmin(<AppUsersPage />)} />
        <Route path={ROUTES.adminRestaurants} element={requireEatsAppManager(<EatsAppRestaurantsPage />)} />
        <Route path={ROUTES.userProfile} element={requireEatsAppUser(<AppUserProfilePage />)} />

        {/* Legacy eats-business / app bookmarks */}
        <Route path="/eats-business/users" element={<Navigate to={ROUTES.adminEmployees} replace />} />
        <Route path="/eats-business/restaurants" element={<Navigate to={ROUTES.adminRestaurants} replace />} />
        <Route path="/eats-business/user/profile" element={<Navigate to={ROUTES.userProfile} replace />} />
        <Route path="/app/users" element={<Navigate to={ROUTES.adminEmployees} replace />} />
        <Route path="/app/restaurants" element={<Navigate to={ROUTES.adminRestaurants} replace />} />
        <Route path="/app/user/profile" element={<Navigate to={ROUTES.userProfile} replace />} />
      </Routes>
    
    </div>
   
  );
};

export default App;
